import React, { useEffect, useState } from 'react';
import { TextField, InputAdornment, IconButton, Table, TableBody, TableCell, TableHead, TableRow, Box, CircularProgress, Paper, TableSortLabel } from '@mui/material';
import { RenderParticipantName } from './Elements';
import { fetchParticipants, Participant, fetchAllParticipantRanks, ParticipantRankRow, RankType } from '../api/contests';
import ClearIcon from '@mui/icons-material/Clear';

const Participants: React.FC = () => {
    const [participants, setParticipants] = useState<Participant[]>([]);
    const [filteredParticipants, setFilteredParticipants] = useState<Participant[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState<{ key: string, direction: 'asc' | 'desc' } | null>(null);
    const [participantRanks, setParticipantRanks] = useState<Map<number, RankType[]>>(new Map());

    useEffect(() => {
        const loadParticipants = async () => {
            try {
                const data = await fetchParticipants();
                setParticipants(data);
                setFilteredParticipants(data);
            } catch (error) {
                console.error('Error fetching participants:', error);
            }
        };
        loadParticipants();
    }, []);

    useEffect(() => {
        const loadParticipantRanks = async () => {
            try {
                const rows: ParticipantRankRow[] = await fetchAllParticipantRanks();
                const m = new Map<number, RankType[]>();
                rows.forEach((row) => {
                    const arr = m.get(row.participant_id) ?? [];
                    arr.push(row.rank_type);
                    m.set(row.participant_id, arr);
                });
                setParticipantRanks(m);
            } catch (error) {
                console.error('Error fetching participant ranks:', error);
            }
        };
        loadParticipantRanks();
    }, []);

    useEffect(() => {
        const filtered = participants.filter((participant) =>
            participant.name?.toLowerCase()?.includes(searchTerm.toLowerCase())
        );
        setFilteredParticipants(filtered);
    }, [searchTerm, participants]);

    const requestSort = (key: string) => {
        let direction: 'asc' | 'desc' = 'asc';
        if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const sortedParticipants = React.useMemo(() => {
        if (sortConfig !== null) {
            return [...filteredParticipants].sort((a, b) => {
                const key = sortConfig.key as keyof Participant;
                const aValue = a[key];
                const bValue = b[key];

                if (aValue === null || aValue === undefined) return 1;
                if (bValue === null || bValue === undefined) return -1;

                if (aValue < bValue) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }
        return filteredParticipants;
    }, [filteredParticipants, sortConfig]);

    const handleClearSearch = () => {
        setSearchTerm('');
    };

    return !participants.length ? (
        <Box display="flex" justifyContent="center" mt={2}>
            <CircularProgress />
        </Box>
    ) : (
        <Box>
            <TextField
                label="Найти участника"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                variant="outlined"
                fullWidth
                margin="normal"
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            {searchTerm && (
                                <IconButton onClick={handleClearSearch}>
                                    <ClearIcon />
                                </IconButton>
                            )}
                        </InputAdornment>
                    ),
                }}
            />

            {/* Scrollable container for the table */}
            <Box component={Paper} sx={{ overflowX: 'auto', maxWidth: '100%', marginTop: 2 }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>
                                <TableSortLabel
                                    active={sortConfig?.key === 'name'}
                                    direction={sortConfig?.direction}
                                    onClick={() => requestSort('name')}
                                >
                                    Имя
                                </TableSortLabel>
                            </TableCell>
                            <TableCell>
                                <TableSortLabel
                                    active={sortConfig?.key === 'age'}
                                    direction={sortConfig?.direction}
                                    onClick={() => requestSort('age')}
                                >
                                    Возраст
                                </TableSortLabel>
                            </TableCell>
                            <TableCell>
                                <TableSortLabel
                                    active={sortConfig?.key === 'gender'}
                                    direction={sortConfig?.direction}
                                    onClick={() => requestSort('gender')}
                                >
                                    Пол
                                </TableSortLabel>
                            </TableCell>
                            <TableCell>
                                <TableSortLabel
                                    active={sortConfig?.key === 'age_group_name'}
                                    direction={sortConfig?.direction}
                                    onClick={() => requestSort('age_group_name')}
                                >
                                    Группа
                                </TableSortLabel>
                            </TableCell>
                            <TableCell>
                                <TableSortLabel
                                    active={sortConfig?.key === 'total_entries'}
                                    direction={sortConfig?.direction}
                                    onClick={() => requestSort('total_entries')}
                                >
                                    Всего заруб
                                </TableSortLabel>
                            </TableCell>
                            <TableCell>
                                <TableSortLabel
                                    active={sortConfig?.key === 'distinct_contests'}
                                    direction={sortConfig?.direction}
                                    onClick={() => requestSort('distinct_contests')}
                                >
                                    Разных заруб
                                </TableSortLabel>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sortedParticipants.map((participant) => (
                            <TableRow key={participant.participant_id}>
                                <TableCell>
                                    {RenderParticipantName(
                                      participant.participant_id,
                                      participant.name,
                                      participant.profile_picture,
                                      true,
                                      participantRanks.get(participant.participant_id) ?? []
                                    )}
                                </TableCell>
                                <TableCell>{participant.age ?? '-'}</TableCell>
                                <TableCell>{participant.gender}</TableCell>
                                <TableCell>{participant.age_group_name}</TableCell>
                                <TableCell>{participant.total_entries}</TableCell>
                                <TableCell>{participant.distinct_contests}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Box>
        </Box>
    );
}

export default Participants;